import styled from "styled-components";
import React from "react";
import ReactPortal from "./react-portal";
import { ReactComponent as Close } from "assets/icons/close.svg";
import { twMerge } from "tailwind-merge";
import cns from "classnames";
import useResponsive from "../helpers/hooks/useResponsive";
import { AnimatePresence, motion } from "framer-motion";
import { useMobileAgent } from "helpers/hooks/use-mobile-agent";

const Wrapper = styled(motion.div)``;
interface Props {
  isFull?: boolean;
  id?: string;
  open: boolean;
  toggle: () => void;
  containerClassName?: string;
  wrapperClassName?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  header?: React.ReactNode | string;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl";
  hideClose?: boolean;
  customStyles?: React.CSSProperties;
  disableBackdrop?: boolean;
  enableFooterShadow?: boolean;
  centered?: boolean;
  zIndex?: number;
  contentWrapperClass?: string;
  dialogWrapperClass?: string;
  isMeeting?: boolean;
  closeButtonClass?: string;
  className?: string;
}

function Modal({
  isFull = false,
  id = "modal-portal-wrapper",
  open,
  toggle,
  children,
  footer,
  header,
  containerClassName,
  wrapperClassName,
  size = "md",
  hideClose = false,
  customStyles = {},
  disableBackdrop = false,
  enableFooterShadow = false,
  centered = false,
  zIndex = 100,
  contentWrapperClass = "",
  dialogWrapperClass = "",
  isMeeting = false,
  closeButtonClass = "",
  className = "",
}: Props) {
  const isMobile = useMobileAgent();
  const enableMobileVersion = isMobile && !centered;
  // if (!open) return null;
  const sizes = {
    sm: "384px",
    md: "28rem",
    lg: "32rem",
    xl: "36rem",
    "2xl": "42rem",
    "3xl": "48rem",
    "4xl": "56rem",
    "5xl": "64rem",
    "6xl": "72rem",
  };
  const modalAnimation = {
    hidden: {
      opacity: 0,
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "tween",
        // stiffness: 500, // Adjust the stiffness for bounce effect
        damping: 5, // Adjust the damping for bounce effect
        duration: 0.1,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.85, // Slightly smaller when exiting
      transition: {
        type: "spring",
        stiffness: 300, // You can adjust these values for the exit animation
        damping: 20, // to control the smoothness and bounce during exit
      },
    },
  };
  if (isMeeting && isMobile) {
    return (
      <ReactPortal wrapperId={id}>
        <AnimatePresence>
          {open && (
            <Wrapper
              tabIndex={-1}
              className={cns(
                "overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 md:inset-0 h-[100dvh] bg-white w-full scrollbar-hide"
              )}
              style={{
                zIndex: 150,
              }}
            >
              {/* modal body starts */}
              <div className="mt-5 p-1">
                {/* modal title */}
                {typeof header === "string" ? (
                  <h1 className="font-bold text-xl">{header}</h1>
                ) : (
                  header
                )}
                {children}
              </div>
              {/* modal body ends */}

              <button
                type="button"
                className="fixed top-1 right-1 bg-white p-1 rounded-full"
                onClick={toggle}
              >
                <Close className="w-5 h-5 stroke-gray-380" />
                <span className="sr-only">Close modal</span>
              </button>
            </Wrapper>
          )}
        </AnimatePresence>
      </ReactPortal>
    );
  }
  if (enableMobileVersion) {
    return (
      <ReactPortal wrapperId={id}>
        <AnimatePresence>
          {open && (
            <Wrapper
              initial={{
                backgroundColor: "rgba(0, 0, 0, 0)",
                backdropFilter: "blur(0px)",
              }}
              animate={{
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                backdropFilter: "blur(5px)",
              }}
              exit={{
                backgroundColor: "rgba(0, 0, 0, 0)",
                backdropFilter: "blur(0px)",
              }}
              tabIndex={-1}
              className={twMerge(
                "overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0  md:inset-0 h-modal md:h-full flex items-center justify-center mobile:px-2 z-[100]",
                isMobile && "h-screen mobile:h-[100dvh]",
                className
              )}
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                backdropFilter: "blur(5px)",
                zIndex: zIndex,
              }}
            >
              {!disableBackdrop && (
                <div
                  area-hidden="true"
                  className="absolute w-full h-full z-[1]"
                  onClick={toggle}
                ></div>
              )}
              <motion.div
                className={cns(
                  `py-4 h-full w-full md:h-auto z-10 relative flex items-center justify-center flex-wrap`,
                  dialogWrapperClass
                )}
                style={{
                  maxWidth: sizes[size || "lg"],
                }}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalAnimation}
                data-testid="modal-wrapper"
              >
                <div
                  className={twMerge(
                    "flex items-start w-full",
                    contentWrapperClass,
                    centered ? "absolute top-1/2 -translate-y-1/2" : ""
                  )}
                >
                  <div
                    className={twMerge(
                      "relative bg-white rounded-xl shadow dark:bg-gray-700 flex-1 border border-[#D9D9E7]",
                      wrapperClassName
                    )}
                  >
                    {/* modal body starts */}
                    <div
                      className={twMerge(
                        "py-6 mobile:py-4",
                        containerClassName
                      )}
                    >
                      {/* modal title */}
                      <div className="px-6">
                        {typeof header === "string" ? (
                          <h1 className="font-bold text-xl">{header}</h1>
                        ) : (
                          header
                        )}
                      </div>
                      <div
                        className="px-6 mobile:px-4"
                        style={{
                          maxHeight: isFull
                            ? "calc(100vh - 80px)"
                            : "calc(100vh - 210px)",
                          overflowY: "auto",
                          ...customStyles,
                        }}
                      >
                        {children}
                      </div>
                      {/* modal footer */}
                      {!!footer && (
                        <div
                          className="px-6"
                          style={{
                            boxShadow: enableFooterShadow
                              ? "1px -7px 17px -6px rgba(0, 0, 0,.12)"
                              : "none",
                          }}
                        >
                          {footer}
                        </div>
                      )}
                    </div>
                    {/* modal body ends */}
                    {!hideClose && (
                      <button
                        type="button"
                        // className="absolute top-0 -right-8 text-white"
                        className={twMerge(
                          "text-gray-380 ml-2 mobile:absolute mobile:right-4 bg-white w-8 h-8 flex items-center justify-center rounded-full",
                          closeButtonClass
                        )}
                        style={{
                          transform: centered ? "translateY(-40px)" : "inherit",
                          top: "-40px",
                        }}
                        onClick={toggle}
                      >
                        <Close className="w-5 h-5 stroke-gray-380" />
                        <span className="sr-only">Close modal</span>
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            </Wrapper>
          )}
        </AnimatePresence>
      </ReactPortal>
    );
  }
  return (
    <ReactPortal wrapperId={id}>
      <AnimatePresence>
        {open && (
          <Wrapper
            initial={{
              backgroundColor: "rgba(0, 0, 0, 0)",
              backdropFilter: "blur(0px)",
            }}
            animate={{
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              backdropFilter: "blur(5px)",
            }}
            exit={{
              backgroundColor: "rgba(0, 0, 0, 0)",
              backdropFilter: "blur(0px)",
            }}
            tabIndex={-1}
            className={twMerge(
              "overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0  md:inset-0 h-modal md:h-full flex items-center justify-center mobile:px-2 z-[100]",
              isMobile && "h-screen mobile:h-[100dvh]",
              className
            )}
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              backdropFilter: "blur(5px)",
              zIndex: zIndex,
            }}
          >
            {!disableBackdrop && (
              <div
                area-hidden="true"
                className="absolute w-full h-full z-[1]"
                onClick={toggle}
              ></div>
            )}
            <motion.div
              className={cns(
                `py-4 h-full w-full md:h-auto z-10 relative`,
                dialogWrapperClass
              )}
              style={{
                maxWidth: sizes[size || "lg"],
              }}
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={modalAnimation}
            >
              <div
                className={twMerge(
                  "flex items-start",
                  contentWrapperClass,
                  centered ? "absolute top-1/2 -translate-y-1/2" : ""
                )}
              >
                <div
                  className={twMerge(
                    "relative bg-white rounded-xl shadow dark:bg-gray-700 flex-1 border border-[#D9D9E7]",
                    wrapperClassName
                  )}
                >
                  {/* modal body starts */}
                  <div
                    className={twMerge("py-6 mobile:py-4", containerClassName)}
                  >
                    {/* modal title */}
                    <div className="px-6">
                      {typeof header === "string" ? (
                        <h1 className="font-bold text-xl">{header}</h1>
                      ) : (
                        header
                      )}
                    </div>
                    <div
                      className="px-6 mobile:px-4"
                      style={{
                        maxHeight: isFull
                          ? "calc(100vh - 80px)"
                          : "calc(100vh - 210px)",
                        overflowY: "auto",
                        ...customStyles,
                      }}
                    >
                      {children}
                    </div>
                    {/* modal footer */}
                    {!!footer && (
                      <div
                        className="px-6"
                        style={{
                          boxShadow: enableFooterShadow
                            ? "1px -7px 17px -6px rgba(0, 0, 0,.12)"
                            : "none",
                        }}
                      >
                        {footer}
                      </div>
                    )}
                  </div>
                  {/* modal body ends */}
                </div>
                {!hideClose && (
                  <button
                    type="button"
                    // className="absolute top-0 -right-8 text-white"
                    className={twMerge(
                      "text-gray-380 ml-2 mobile:absolute mobile:right-4 mobile:top-12 bg-white w-8 h-8 flex items-center justify-center rounded-full",
                      centered ? "mobile:top-0" : "",
                      closeButtonClass
                    )}
                    style={{
                      transform: centered ? "translateY(-40px)" : "inherit",
                    }}
                    onClick={toggle}
                  >
                    <Close className="w-5 h-5 stroke-gray-380" />
                    <span className="sr-only">Close modal</span>
                  </button>
                )}
              </div>
            </motion.div>
          </Wrapper>
        )}
      </AnimatePresence>
    </ReactPortal>
  );
}

export default Modal;
